import { all, put, takeLatest } from 'redux-saga/effects';
import { RequestQueryBuilder, CondOperator } from '@nestjsx/crud-request';
import {
  DELETE_USER_RATING,
  EDIT_USER,
  GET_REGISTERED_VENUE_OWNERS,
  GET_TAGS,
  GET_USERS,
  GET_USERS_RATING_BY_ROLE,
  GET_USER_BY_COGNITO_ID,
  UPLOAD_PROFILE_PICTURE,
  GET_VENDOR_USERS,
  GET_USERS_VENUE_VENDORS,
  DISABLE_USER_BY_TERMS_ANDS_CONDITIONS,
  GET_VENDORS_BY_VENUE,
  CREATE_USER_ASSET,
  UPDATE_USER_ASSET,
  DELETE_USER_ASSET,
  GET_USER_BY_ID,
} from './constants';
import Api from '../../commons/api';
import * as UserActions from './action';
import * as VenueActions from '../venues/action';
import { APP_ROUTES } from '../../constants/app-routes';
import { onDisplayAlert } from '../../utils/alerts/alerts';
import { GROUP_USER } from '../../config/appGroupConfig';
import { USER_STATUS } from '../../constants/user';

const controller = 'users';

function* fetchGetUserByCognitoId({ payload }) {
  try {
    const { cognitoId } = payload;
    const qb = RequestQueryBuilder.create()
      .setFilter({
        field: 'cognito_id',
        operator: CondOperator.EQUALS,
        value: cognitoId,
      })
      .setJoin({ field: 'role' })
      .setJoin({ field: 'notifications' })
      .setJoin({ field: 'venue' })
      .setJoin({ field: 'venue.social_network' })
      .setJoin({ field: 'stripe_details' })
      .setJoin({ field: 'user_tags' })
      .setJoin({ field: 'user_tags.category' })
      .setJoin({ field: 'assets' })
      .setJoin({ field: 'vendor_social_network' });

    const { data } = yield Api.get(`${controller}?${qb.query()}`);
    const [user] = data;

    if (user.state === USER_STATUS.INACTIVE) {
      yield put(UserActions.resetUserState());
      window.localStorage.clear();
      window.sessionStorage.clear();
      window.location.href = '/';
    } else {
      user.user_tags = user.user_tags?.filter((ut) => ut.category.state === true);
      localStorage.setItem('user', JSON.stringify(user));

      // Attention!!!, do not delete this part. It is important. Thanks.
      if (payload.navigate && user.role.id === 2 && !user.venue) {
        onDisplayAlert({
          title: 'Venue Information',
          description: 'Before continuing you must enter the details of your venue',
          type: 'info',
        });
        payload.navigate(APP_ROUTES.MANAGE_VENUES);
      } else if (payload.navigate) payload.navigate(APP_ROUTES.DASHBOARD);

      if (user?.venue && user.role.id === 2) {
        yield put(VenueActions.setCurrentVenueUser(user.venue));
        yield put(VenueActions.getPicturesByVenue(user.venue.id));
      }
      yield put(UserActions.getUserByCognitoIdSuccess(data[0]));
    }
  } catch (err) {
    console.log({ err });
    yield put(UserActions.getUserByCognitoIdFailed(err));
  }
}

function* fetchGetRegisteredVenueOwners({ payload }) {
  try {
    const { filter } = payload;
    const { data } = yield Api.post(`${controller}/${controller}/registered-venue-owners`, filter);
    yield put(UserActions.getRegisteredVenueOwnersSuccess(data));
  } catch (err) {
    yield put(UserActions.getRegisteredVenueOwnersSuccess(err));
  }
}

function* getTags() {
  try {
    const { data } = yield Api.get(`${controller}/${controller}/tags`);
    yield put(UserActions.getTagsSuccess(data));
  } catch (err) {
    yield put(UserActions.getTagsFailed(err));
  }
}

function* getUsers() {
  try {
    const qb = RequestQueryBuilder.create().setJoin({ field: 'role' });
    const { data } = yield Api.get(`${controller}?${qb.query()}`);
    yield put(UserActions.getUsersSuccess(data));
  } catch (err) {
    yield put(UserActions.getUsersFailed(err));
  }
}

function* editUser({ payload }) {
  try {
    console.log('🍉 editUser ', payload);
    const { data } = yield Api.put(`${controller}/${controller}/update-user`, payload.user);
    yield put(UserActions.getUserByCognitoId(data.cognito_id));
    yield put(UserActions.editUserSuccess(data));
  } catch (err) {
    yield put(UserActions.editUserFailed(err));
  }
}

function* fetchDisableUserByTermsAndConditions({ payload }) {
  try {
    const { message, userId, eventId } = payload;
    const { data } = yield Api.patch(
      `${controller}/${controller}/disable?userId=${userId}&eventId=${eventId}`,
      { message },
    );
    yield put(UserActions.disableUserByTermsAndConditionSuccess(userId));
    onDisplayAlert({
      title: 'User disabled',
      description: 'This user has been disabled',
      type: 'success',
    });
  } catch (err) {
    yield put(UserActions.disableUserByTermsAndConditionFailed(err));
    onDisplayAlert({
      title: 'Error disabling',
      description: 'This user has not been disabled, sorry.',
      type: 'error',
    });
  }
}

function* uploadPicture({ payload }) {
  try {
    const { data } = yield Api.postMultipart(
      `${controller}/${controller}/upload/photo`,
      payload.user,
    );
    yield put(UserActions.getUserByCognitoId(data.sub));
    yield put(UserActions.uploadPictureSuccess(data));
  } catch (err) {
    yield put(UserActions.uploadPictureFailed(err));
  }
}

function* getRatingsByUserRole() {
  try {
    const qb = RequestQueryBuilder.create()
      .setJoin({ field: 'role' })
      .setJoin({ field: 'venue' })
      .setJoin({ field: 'ratingsReceived' })
      .setJoin({ field: 'ratingsReceived.replies' })
      .setFilter({
        field: 'role.name',
        operator: CondOperator.IN,
        value: [GROUP_USER.VENDOR, GROUP_USER.VENUE],
      })
      .sortBy({ field: 'ratingsReceived.created_at', order: 'DESC' });
    const { data } = yield Api.get(`${controller}?${qb.query()}`);
    yield put(UserActions.getUsersRatingByRoleSuccess(data));
  } catch (err) {
    yield put(UserActions.getUserByCognitoIdFailed(err));
  }
}

function* deleteUserRating({ payload }) {
  try {
    const { data } = yield Api.patch(
      `${controller}/${controller}/delete/user-review`,
      payload.rating,
    );
    yield put(UserActions.deleteUserRatingSuccess(data));
    yield put(UserActions.getUsersRatingByRole());
  } catch (err) {
    yield put(UserActions.getUserByCognitoIdFailed(err));
  }
}

function* getVendorUsers() {
  try {
    const qb = RequestQueryBuilder.create().setJoin({ field: 'role' }).setFilter({
      field: 'role.id',
      operator: CondOperator.EQUALS,
      value: 1,
    });
    const { data } = yield Api.get(`${controller}?${qb.query()}`);
    yield put(UserActions.getVendorUsersSuccess(data));
  } catch (err) {
    yield put(UserActions.getVendorUsersFailed(err));
  }
}

function* getVenueVendorUser() {
  try {
    const qb = RequestQueryBuilder.create()
      .setJoin({ field: 'role' })
      .setFilter({
        field: 'role.name',
        operator: CondOperator.IN,
        value: [GROUP_USER.VENDOR, GROUP_USER.VENUE],
      });
    const { data } = yield Api.get(`${controller}?${qb.query()}`);
    yield put(UserActions.getVenueVendorUsersSuccess(data));
  } catch (err) {
    yield put(UserActions.getVenueVendorUsersFailed(err));
  }
}

function* getVendorsByVenue({ payload }) {
  try {
    const { venueId } = payload;

    const qb = RequestQueryBuilder.create()
      .setJoin({ field: 'role' })
      .setJoin({ field: 'transactions', select: ['id', 'created_at', 'type', 'amount'] })
      .setJoin({ field: 'transactions.event', select: ['name', 'fk_venue'] })
      .setFilter({
        field: 'role.name',
        operator: CondOperator.EQUALS,
        value: GROUP_USER.VENDOR,
      })
      .setFilter({
        field: 'transactions.event.fk_venue',
        operator: CondOperator.EQUALS,
        value: venueId,
      });
    const { data } = yield Api.get(`${controller}?${qb.query()}`);
    yield put(UserActions.getVendorsByVenueSuccess(data));
  } catch (err) {
    yield put(UserActions.getVendorsByVenueFailed(err));
  }
}

function* createUserAsset({ payload }) {
  try {
    const { data } = yield Api.post(`${controller}/${controller}/add-asset/`, payload.asset);

    yield put(UserActions.createUserAssetSuccess(data));
  } catch (err) {
    console.log('error', err);
    yield put(UserActions.createUserAssetFailed(err));
  }
}

function* deleteUserAsset({ payload }) {
  try {
    const { data } = yield Api.get(`${controller}/${controller}/delete-asset/${payload.assetId}`);

    yield put(UserActions.deleteUserAssetSuccess(data));
  } catch (err) {
    console.log('error', err);
    yield put(UserActions.deleteUserAssetFailed(err));
  }
}

function* getUserById({ payload }) {
  try {
    const qb = RequestQueryBuilder.create()
      .setJoin({ field: 'user_tags' })
      .setJoin({ field: 'vendor_social_network' })
      .setJoin({ field: 'user_tags.category' });

    const { data } = yield Api.get(`${controller}/${controller}/${payload.userId}?${qb.query()}`);
    yield put(UserActions.getUserByIdSuccess(data));
  } catch (err) {
    console.log('error', err);
    yield put(UserActions.getUserByIdFailed(err));
  }
}

function* ActionWatcher() {
  yield takeLatest(GET_USER_BY_COGNITO_ID, fetchGetUserByCognitoId);
  yield takeLatest(GET_TAGS, getTags);
  yield takeLatest(GET_USERS, getUsers);
  yield takeLatest(EDIT_USER, editUser);
  yield takeLatest(UPLOAD_PROFILE_PICTURE, uploadPicture);
  yield takeLatest(GET_REGISTERED_VENUE_OWNERS, fetchGetRegisteredVenueOwners);
  yield takeLatest(GET_USERS_RATING_BY_ROLE, getRatingsByUserRole);
  yield takeLatest(DELETE_USER_RATING, deleteUserRating);
  yield takeLatest(GET_VENDOR_USERS, getVendorUsers);
  yield takeLatest(GET_USERS_VENUE_VENDORS, getVenueVendorUser);
  yield takeLatest(DISABLE_USER_BY_TERMS_ANDS_CONDITIONS, fetchDisableUserByTermsAndConditions);
  yield takeLatest(GET_VENDORS_BY_VENUE, getVendorsByVenue);
  yield takeLatest(CREATE_USER_ASSET, createUserAsset);
  yield takeLatest(DELETE_USER_ASSET, deleteUserAsset);
  yield takeLatest(GET_USER_BY_ID, getUserById);
}

export default function* UserSaga() {
  yield all([ActionWatcher()]);
}
